/**
 * Actuarial valuation engine — CIA Standards of Practice style.
 *
 * Replaces the original "annuity over a flat life-expectancy term" shortcut
 * with a proper survival-weighted life annuity:
 *
 *   PV = Σ_t  payment_t · v(t) · (probability the payment is made)
 *
 * using CPM2014 generational mortality (full 2-D CPM-B improvement) for the
 * survival weights, select-and-ultimate discount rates, indexation, and a
 * guarantee period (e.g. single life guaranteed 10 years — the PSPP/MPP normal
 * form). Payments are valued exactly monthly (no Woolhouse approximation).
 *
 * Mortality table is sector-selectable (Composite / Public / Private): public
 * suits government plans (federal & provincial), private suits corporate plans.
 *
 * Caveats vs. a formal actuarial valuation:
 *  - Single life only (no joint-and-survivor); no pre-retirement death benefit.
 *  - Deferred annuities valued at commencement (select period from commencement).
 */
import {
  BASE_YEAR, BASE_AGE, MAX_AGE,
  IMP_FIRST_YEAR, IMP_LAST_YEAR,
  CPM2014_COMPOSITE_MALE, CPM2014_COMPOSITE_FEMALE,
  CPM2014_PUBLIC_MALE, CPM2014_PUBLIC_FEMALE,
  CPM2014_PRIVATE_MALE, CPM2014_PRIVATE_FEMALE,
  CPM_B_MALE_2D, CPM_B_FEMALE_2D,
} from './mortality-cpm2014.js'

const SECTOR_TABLES = {
  composite: { male: CPM2014_COMPOSITE_MALE, female: CPM2014_COMPOSITE_FEMALE },
  public: { male: CPM2014_PUBLIC_MALE, female: CPM2014_PUBLIC_FEMALE },
  private: { male: CPM2014_PRIVATE_MALE, female: CPM2014_PRIVATE_FEMALE },
}

function baseTable(gender, sector) {
  const s = SECTOR_TABLES[sector] || SECTOR_TABLES.composite
  return gender === 'Female' ? s.female : s.male
}
function impTable(gender) {
  return gender === 'Female' ? CPM_B_FEMALE_2D : CPM_B_MALE_2D
}

// Memoise the cumulative generational q_x by (sector|gender|age|year) so the
// monthly survival loop doesn't recompute the improvement product each call.
const _qxCache = new Map()

// Mortality rate at integer `age` in calendar year `calYear`. With `improve`
// true (default), applies the full 2-D CPM-B scale generationally from 2014
// (each year uses that year's age-specific rate, held at the 2030 ultimate
// after 2030). `sector` selects the CPM2014 base table.
function projectedQx(age, gender, calYear, improve = true, sector = 'composite') {
  const a = Math.max(BASE_AGE, Math.min(MAX_AGE, Math.floor(age)))
  const baseQ = baseTable(gender, sector)[a - BASE_AGE]
  if (!improve || calYear <= BASE_YEAR) return baseQ
  const yr = Math.floor(calYear)
  const key = sector + '|' + gender + '|' + a + '|' + yr
  const cached = _qxCache.get(key)
  if (cached !== undefined) return cached
  const row = impTable(gender)[a - BASE_AGE]
  const lastCol = IMP_LAST_YEAR - IMP_FIRST_YEAR
  let factor = 1
  for (let y = BASE_YEAR; y < yr; y++) {
    const col = Math.min(Math.max(y, IMP_FIRST_YEAR), IMP_LAST_YEAR) - IMP_FIRST_YEAR
    factor *= 1 - row[Math.min(col, lastCol)]
  }
  const q = baseQ * factor
  _qxCache.set(key, q)
  return q
}

/**
 * Probability of surviving `dur` years (may be fractional) starting from EXACT
 * (possibly fractional) age `age` in calendar year `calYear`. Generational:
 * each age-year uses that future calendar year's improved rate, with a constant
 * force of mortality assumed within each integer age-year so fractional ages /
 * durations are handled exactly.
 */
function surviveFractional(age, gender, calYear, dur, improve = true, sector = 'composite') {
  let p = 1
  let a = age
  let y = calYear
  let remaining = dur
  let guard = 0
  while (remaining > 1e-9 && a < MAX_AGE && guard++ < 200) {
    const intAge = Math.floor(a)
    const yearLeft = intAge + 1 - a // fraction of this age-year remaining
    const step = Math.min(remaining, yearLeft)
    const q = projectedQx(intAge, gender, Math.floor(y), improve, sector)
    p *= Math.pow(1 - q, step) // constant force within the age-year
    a += step
    y += step
    remaining -= step
  }
  return p
}

/**
 * Survival curve from the current age (integer-step), retained for the in-pay
 * life-expectancy display. tpx[t] = probability of being alive t years out.
 */
export function survivalCurve(currentAge, gender, valuationYear, improve = true, sector = 'composite') {
  const start = Math.round(currentAge)
  const tpx = [1]
  let p = 1
  for (let t = 0; start + t < MAX_AGE; t++) {
    const q = projectedQx(start + t, gender, valuationYear + t, improve, sector)
    p *= 1 - q
    tpx.push(p)
  }
  return tpx
}

// Select-and-ultimate discount factor to time t (years): rate1 for the first
// `selectYears`, rate2 thereafter. Rates are per-annum decimals.
function discountFactor(t, rate1, rate2, selectYears) {
  const sel = Math.min(t, selectYears)
  const ult = Math.max(0, t - selectYears)
  return Math.pow(1 + rate1, -sel) * Math.pow(1 + rate2, -ult)
}

/**
 * Present value (per $1 of annual pension) of a single-life pension annuity.
 *
 *  opts:
 *   currentAge, gender, valuationYear
 *   nominalRate1/2, selectYears  — discount rates (per annum), select period
 *   indexationRate               — annual indexing of payments in pay (decimal)
 *   guaranteeYears               — payments certain for this many years from start
 *   deferYears                   — 0 for in-pay; (retirementAge − currentAge) for deferred
 *   paymentsPerYear              — 12 for monthly pensions (Woolhouse adj), 1 for annual
 *
 * Indexed payments grow at `indexationRate`; the net effect is a real discount
 * rate, which is the CIA convention for fully-indexed plans.
 */
export function pensionAnnuityFactor({
  currentAge,
  gender = 'Male',
  valuationYear,
  nominalRate1,
  nominalRate2,
  selectYears = 20,
  // In-pay indexation (split at the select boundary). indexationRate2 defaults
  // to indexationRate when not supplied.
  indexationRate = 0,
  indexationRate2 = null,
  guaranteeYears = 0,
  deferYears = 0,
  paymentsPerYear = 12,
  // Pre-commencement (wage) escalation for a today's-dollar input pension, also
  // split at the select boundary. preCommencementGrowth2 defaults to ...Growth.
  preCommencementGrowth = 0,
  preCommencementGrowth2 = null,
  // Cap the number of payment years (for a temporary/bridge benefit).
  maxPaymentYears = Infinity,
  // Apply CPM-B generational improvement (default on — full 2-D scale).
  mortalityImprovement = true,
  // CPM2014 base table: 'composite' | 'public' | 'private'.
  mortalitySector = 'composite',
  // Discount-rate term structure. TRUE (default) measures the select period from
  // the VALUATION date — the CIA calendar-based term structure (rate1 for calendar
  // years 1..N from valuation, rate2 after), which is the correct reading. FALSE
  // measures it from the COMMENCEMENT date (an "annuity factor at retirement age"
  // shortcut). The two agree when rate1≈rate2, but diverge sharply for a large
  // select/ultimate gap or a long deferral: against real BCH reports the from-
  // valuation basis is consistent (~−3% across gaps 0.65–2.5 and deferrals 6–25y),
  // whereas the commencement basis ranged −1% to +9%. See PROJECT_NOTES.
  selectFromValuation = true,
}) {
  const idx2 = indexationRate2 == null ? indexationRate : indexationRate2
  const wage2 = preCommencementGrowth2 == null ? preCommencementGrowth : preCommencementGrowth2
  const imp = mortalityImprovement
  const sec = mortalitySector
  const m = Math.max(1, Math.round(paymentsPerYear)) // payments per year (12 = monthly)

  // Exact (fractional) deferral to commencement; no rounding.
  const defer = Math.max(0, deferYears)
  const surviveToStart = surviveFractional(currentAge, gender, valuationYear, defer, imp, sec)

  const ageAtStart = currentAge + defer
  const yearsLeft = Math.max(0, MAX_AGE - ageAtStart)
  const lastYear = Math.min(yearsLeft, maxPaymentYears)

  // Discount uses the CIA calendar-based term structure from the valuation date
  // (see `selectFromValuation`): a payment at time (defer + k) is discounted by
  // discountFactor(defer + k). The `deferralDiscount` below is retained only for
  // the legacy commencement-basis path (selectFromValuation = false), where the
  // annuity's own select period runs from commencement and is then discounted
  // back over the deferral. Indexation is always calendar-based from valuation.
  const deferralDiscount = discountFactor(defer, nominalRate1, nominalRate2, selectYears)

  // Exact monthly-in-advance valuation: each month's payment (1/m of the annual
  // amount) is discounted and weighted by survival to that exact point. No
  // Woolhouse approximation. Indexation steps up once per completed in-pay year.
  let pv = 0
  let idxFactor = 1
  let survT = surviveToStart // survival to commencement + k years
  for (let j = 0; ; j++) {
    const k = j / m // years since commencement
    if (k >= lastYear) break
    const tCal = defer + k // years from valuation
    const weight = k < guaranteeYears ? surviveToStart : survT
    const disc = selectFromValuation
      ? discountFactor(defer + k, nominalRate1, nominalRate2, selectYears)
      : deferralDiscount * discountFactor(k, nominalRate1, nominalRate2, selectYears)
    pv += (1 / m) * disc * idxFactor * weight

    // Advance survival by one month.
    survT *= surviveFractional(ageAtStart + k, gender, valuationYear + tCal, 1 / m, imp, sec)
    // Step indexation at each completed in-pay year (anniversary increase).
    if ((j + 1) % m === 0) {
      const yrCompleted = (j + 1) / m - 1
      idxFactor *= 1 + (defer + yrCompleted < selectYears ? indexationRate : idx2)
    }
  }
  pv = Math.max(0, pv)

  // Scale a today's-dollar benefit up to its commencement-date amount (wage
  // escalation over the deferral, split at the select boundary).
  if (defer > 0 && (preCommencementGrowth > 0 || wage2 > 0)) {
    const sel = Math.min(defer, selectYears)
    const ult = Math.max(0, defer - selectYears)
    pv *= Math.pow(1 + preCommencementGrowth, sel) * Math.pow(1 + wage2, ult)
  }
  return pv
}
